import { Button } from '@gerdau/hefesto/Button';
import { SearchField } from '@gerdau/hefesto/SearchField';
import { Select } from '@gerdau/hefesto/Select';
import { TSelectOptionProps } from '@gerdau/hefesto/Select/Select.types';
import { Title } from '@gerdau/hefesto/Title';
import moment from 'moment';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomerSelect from '../../../../components/CustomerSelect/Facelift';
import { ApplicationState } from '../../../../store';
import {
  EOrderTab,
  IBilledOrderData,
  ILongSteelOrderData,
  ILongSteelOrdersState,
  IOrderDivision,
  IOrdersInitialInputValues
} from '../../../../store/ducks/long-steel-orders/types';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { ITaggingParams } from '../../../../store/ducks/tagging/types';
import { ECustomerTypeView } from '../../../../store/ducks/user/types';
import {
  Container,
  FilterContent,
  OrderFilterBlock,
  SearchAndActionsBlock
} from './styles';

interface IBilledOrdersFiltersProps {
  orderTab?: EOrderTab;
  orderState: ILongSteelOrdersState;
  customerViewType: ECustomerTypeView;
  onCleanFilters(): void;
  loadOrders(
    states: string[],
    emitterIds: string[],
    startDate?: string,
    endDate?: string
  ): void;
  isFiltered: boolean;
  setFilteredOrders(filteredOrders: ILongSteelOrderData[]): void;
  receiverBilledId: string;
  setReceiverBilledId(receiverId: string): void;
  emitterIds: string[];
  setEmitterIds(emitterIds: string[]): void;
}

const BilledOrdersFilter: React.FC<IBilledOrdersFiltersProps> = ({
  orderState,
  receiverBilledId,
  setReceiverBilledId,
  isFiltered,
  setFilteredOrders,
  customerViewType,
  emitterIds,
  setEmitterIds,
  onCleanFilters,
  loadOrders,
  orderTab
}) => {
  const ordersTypeProductList: TSelectOptionProps[] =
    orderState?.divisions?.map((division: IOrderDivision) => ({
      key: division.division,
      value: division.division,
      label: division.divisionDescription
    }));
  const dispatch = useDispatch();
  const customerState = useSelector(
    (appState: ApplicationState) => appState.customers
  );
  const isUserAdmin = useSelector(
    (state: ApplicationState) => state.user.isManager
  );
  const userState = useSelector((state: ApplicationState) => state.user);
  const customers = useSelector((state: ApplicationState) =>
    state.customers.customers.filter(
      (customer) =>
        isUserAdmin ||
        userState.roles.ordersViewCustomers.includes(customer.customerId)
    )
  );
  const [alreadyInitialized, setAlreadyInitialized] = useState(false);
  const [startSelectedDate, setStartSelectedDate] = useState<moment.Moment>(
    orderState?.initialInputValues?.startDate
      ? moment(orderState?.initialInputValues?.startDate)
      : moment()
  );
  const [endSelectedDate, setEndSelectedDate] = useState<moment.Moment>(
    orderState?.initialInputValues?.endDate
      ? moment(orderState?.initialInputValues?.endDate)
      : moment()
  );
  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [selectedStates, setSelectedStates] = useState<string[]>(
    orderState?.initialInputValues?.states || []
  );

  useEffect(() => {
    const setInitialInputValues = (
      initialInputValues: IOrdersInitialInputValues
    ) => {
      setSelectedStates(initialInputValues.states);
      setEmitterIds(initialInputValues.emitterIds);
      setReceiverBilledId(initialInputValues.receiverId);
      setStartSelectedDate(moment(initialInputValues.startDate));
      setEndSelectedDate(moment(initialInputValues.endDate));
      setSelectedDivisions([]);
      setSearchInput('');
      setAlreadyInitialized(true);
    };

    if (
      orderState.initialInputValues &&
      !alreadyInitialized &&
      !orderState.loading &&
      orderState.initialized &&
      customers?.length > 0
    ) {
      setInitialInputValues(orderState.initialInputValues);
    } else if (orderState.loading) {
      setAlreadyInitialized(false);
    }
  }, [
    orderState.initialInputValues,
    orderState.loading,
    orderState.initialized,
    alreadyInitialized,
    orderTab,
    customers,
    customerViewType,
    setReceiverBilledId,
    setEmitterIds
  ]);

  const prepareAndSendTagging = () => {
    const taggingParams: ITaggingParams = {
      screenName: 'LG:MeusPedidos:AcosLongos:PedidosFaturados',
      itemName: 'button:limpar_filtros',
      itemDescription: 'meus_pedidos_acosLongos_pedidosFaturados_limparFiltros',
      event: 'click'
    };
    dispatch(sendTagging(taggingParams));
  };

  const onClickClean = () => {
    setAlreadyInitialized(false);
    onCleanFilters();
    prepareAndSendTagging();
  };

  const hasChangedEmitterIds = (): boolean => {
    const initialEmitterIds = orderState?.initialInputValues?.emitterIds || [];
    const hasNewEmitter = emitterIds.some(
      (emitter) => !initialEmitterIds.includes(emitter)
    );

    return hasNewEmitter || emitterIds?.length !== initialEmitterIds?.length;
  };

  const hasChangedReceiverId = (): boolean => {
    const initialReceiverId = orderState?.initialInputValues?.receiverId || '';

    return receiverBilledId !== '' && receiverBilledId !== initialReceiverId;
  };

  const canCleanFilters = (): boolean => {
    const initialValues = orderState.initialInputValues;
    const startDate = startSelectedDate
      ? (startSelectedDate as moment.Moment).format('YYYY-MM-DD')
      : '';
    const endDate = endSelectedDate
      ? (endSelectedDate as moment.Moment).format('YYYY-MM-DD')
      : '';
    const hasChangedDates =
      (initialValues?.startDate && initialValues?.startDate !== startDate) ||
      (initialValues?.endDate && initialValues?.endDate !== endDate);

    return (
      isFiltered ||
      hasChangedDates ||
      hasChangedEmitterIds() ||
      hasChangedReceiverId() ||
      selectedDivisions?.length > 0 ||
      searchInput !== ''
    );
  };

  const onFilter = (
    states: string[],
    emitterIds: string[],
    receiverId: string,
    startSelectedDate: moment.Moment,
    endSelectedDate: moment.Moment,
    selectedDivisions: string[],
    searchInput: string
  ) => {
    onBilledOrdersFilter(
      states,
      emitterIds,
      receiverId,
      startSelectedDate,
      endSelectedDate,
      selectedDivisions,
      searchInput
    );
  };

  const filterByReceivers = (
    orders: ILongSteelOrderData[],
    filterBilledReceiverId: string
  ): ILongSteelOrderData[] => {
    return orders?.filter(
      (order) => order?.shipTo?.code === filterBilledReceiverId
    );
  };

  const filterByEmitters = (
    orders: ILongSteelOrderData[]
  ): ILongSteelOrderData[] => {
    const emitterId = emitterIds[0];
    return orders?.filter((order) => order?.soldTo?.code === emitterId);
  };

  const canLoadOrders = (): boolean => {
    const startDate = startSelectedDate.format('YYYY-MM-DD');
    const endDate = endSelectedDate.format('YYYY-MM-DD');
    return (
      orderState?.items?.length === 0 ||
      (customerViewType === ECustomerTypeView.EMITTER &&
        hasChangedEmitterIds()) ||
      ((customerViewType === ECustomerTypeView.RECEIVER ||
        customerViewType === ECustomerTypeView.FABRICATION_RECEIVER) &&
        hasChangedReceiverId()) ||
      (orderTab === EOrderTab.BILLED &&
        (startDate !== orderState.initialInputValues?.startDate ||
          endDate !== orderState.initialInputValues?.endDate))
    );
  };

  const onBilledOrdersFilter = (
    states: string[],
    emitterIds: string[],
    receiverId: string,
    startSelectedDate: moment.Moment,
    endSelectedDate: moment.Moment,
    selectedDivisions: string[],
    searchInput: string
  ) => {
    const startDate = startSelectedDate.format('YYYY-MM-DD');
    const endDate = endSelectedDate.format('YYYY-MM-DD');

    if (canLoadOrders()) {
      const customerIds: string[] =
        customerViewType === ECustomerTypeView.EMITTER
          ? emitterIds
          : [receiverId];

      loadOrders(states, customerIds, startDate, endDate);
    }
    const filterBySearchInput = (
      orders: IBilledOrderData[]
    ): IBilledOrderData[] => {
      return orders?.filter((order) => {
        return (
          order.billingHistory.sku
            .toString()
            .toUpperCase()
            .includes(searchInput) ||
          order.invoiceZvoi.toUpperCase().includes(searchInput) ||
          order.billingHistory.customerSKU
            .toUpperCase()
            .includes(searchInput) ||
          order.billingHistory.salesOrder
            .toString()
            .toUpperCase()
            .includes(searchInput) ||
          order.billingHistory.customerOrder
            .toUpperCase()
            .includes(searchInput) ||
          order.billingHistory.materialDescription
            .toUpperCase()
            .includes(searchInput) ||
          order.invoiceNumber.toUpperCase().includes(searchInput) ||
          order.nfeAccessKey.toUpperCase().includes(searchInput)
        );
      });
    };

    const filterByDivision = (
      orders: IBilledOrderData[]
    ): IBilledOrderData[] => {
      return orders?.filter((order) =>
        selectedDivisions?.includes(order?.billingHistory?.division)
      );
    };

    let filtered: IBilledOrderData[] = orderState.items as IBilledOrderData[];

    if (customerViewType === ECustomerTypeView.EMITTER && receiverId) {
      filtered = filterByReceivers(filtered, receiverId) as IBilledOrderData[];
    }

    if (
      (customerViewType === ECustomerTypeView.RECEIVER ||
        customerViewType === ECustomerTypeView.FABRICATION_RECEIVER) &&
      emitterIds?.length > 0
    ) {
      filtered = filterByEmitters(filtered) as IBilledOrderData[];
    }

    if (selectedDivisions?.length > 0) {
      filtered = filterByDivision(filtered);
    }

    if (searchInput !== '') {
      filtered = filterBySearchInput(filtered);
    }

    setFilteredOrders(filtered);
  };

  return (
    <Container>
      <FilterContent>
        <Title level={3}>Filtros</Title>
        <SearchAndActionsBlock>
          <SearchField
            label="Procurar pedido"
            placeholder="Pesquise por Nº do pedido, Nº da OV Gerdau, Nº da nota fiscal, código ou descrição de material"
            name="search"
            inputValue={searchInput}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearchInput(e.target.value);
              onFilter(
                selectedStates,
                emitterIds,
                receiverBilledId,
                startSelectedDate,
                endSelectedDate,
                selectedDivisions,
                e.target.value.toUpperCase()
              );
            }}
            disabled={orderState.loading || orderState?.items?.length === 0}
          />
        </SearchAndActionsBlock>

        <div className="inputs-container">
          <div>
            <OrderFilterBlock>
              {customerViewType === ECustomerTypeView.EMITTER ? (
                <CustomerSelect
                  data-testid="select-recebedor-pedido-faturado"
                  title={'Recebedor do pedido'}
                  placeholder="Selecionar recebedor"
                  disabled={
                    customerState.loading ||
                    orderState?.loading ||
                    !orderState?.receivers ||
                    orderState?.receivers?.length === 0
                  }
                  customers={orderState.receivers || []}
                  onSelect={(customerId) => {
                    setReceiverBilledId(customerId);
                    onFilter(
                      selectedStates,
                      emitterIds,
                      customerId,
                      startSelectedDate,
                      endSelectedDate,
                      selectedDivisions,
                      searchInput.toUpperCase()
                    );
                  }}
                  selectedCustomerId={receiverBilledId}
                  customStyles={{ selectTitleFontSize: '0.8rem' }}
                ></CustomerSelect>
              ) : (
                <CustomerSelect
                  data-testid="select-emissor-pedido-faturado"
                  title={'Emissor do pedido'}
                  placeholder="Selecionar emissor"
                  disabled={orderState.loading || customerState.loading}
                  customers={orderState.emitters || []}
                  onSelect={(customerId) => {
                    setEmitterIds([customerId]);
                    onFilter(
                      selectedStates,
                      emitterIds,
                      customerId[0],
                      startSelectedDate,
                      endSelectedDate,
                      selectedDivisions,
                      searchInput.toUpperCase()
                    );
                  }}
                  selectedCustomerId={
                    emitterIds?.length ? emitterIds[0] : undefined
                  }
                  customStyles={{ selectTitleFontSize: '0.8rem' }}
                ></CustomerSelect>
              )}

              <Select
                data-testid="select-tipo-produto-faturado"
                isMultipleSelect
                label="Tipo de produto"
                placeholder={`- Selecione -`}
                name="TypeOrdersProductSelect"
                options={ordersTypeProductList}
                value={selectedDivisions}
                onSelect={(value: string | string[]) => {
                  setSelectedDivisions(value as string[]);
                  onFilter(
                    selectedStates,
                    emitterIds,
                    receiverBilledId,
                    startSelectedDate,
                    endSelectedDate,
                    value as string[],
                    searchInput.toUpperCase()
                  );
                }}
                disabled={orderState.loading || orderState?.items?.length === 0}
              ></Select>
            </OrderFilterBlock>

            <Button
              type="button"
              name="btnCleanFilters"
              onClick={onClickClean}
              variant="secondary"
              disabled={orderState.loading || !canCleanFilters()}
              loading={orderState.loading}
            >
              Limpar filtros
            </Button>
          </div>
        </div>
      </FilterContent>
    </Container>
  );
};

export default BilledOrdersFilter;
